<script setup lang="ts">
import DollarValue from '@/components/common/DollarValue.vue'
import Capsule from '@/components/common/Capsule.vue'
import { useRouter } from 'vue-router'

import { OutlinedDownIcon } from '@/components/icons'
import {useThemeStore, useValueDataStore} from '@/stores'
import { computed } from 'vue'
import {storeToRefs} from "pinia";

interface IProps {
  isMain: boolean
  totalValue: number
  currentQuarterValue: number
  opportunityValue: number
  division?: string
  path?: string
}

const props = defineProps<IProps>()
const router = useRouter()

const valueStore = useValueDataStore();
const {valuePeriods} = storeToRefs(valueStore);

const themeStore = useThemeStore()
const { colorVidacorp, colorBackgroundBright, colorText } = themeStore.themeColors

const isCurrentPath = computed(() => {
  return props.path !== undefined && router.currentRoute.value.path === props.path
})

const handleClick = () => {
  if (props.path) {
    router.push(props.path)
  }
}
</script>

<template>
  <Capsule
    :isMain="props.isMain"
    mode="static"
    :childrenCount="2"
    :background="props.isMain ? 'dark' : 'light'"
  >
    <template #1>
      <div class="value-outer">
        <div class="value-inner">
          <slot name="value-title" />
          <slot name="chart" />
          <div></div>
        </div>
        <div class="dollar-value">
          <DollarValue :value="props.totalValue" fontSize="2.1875rem" lineHeight="2.1875rem" :privacy="true" :infoColor="props.division === 'DBG' ? colorBackgroundBright : colorText">
            <template #info>
              Total {{ props.division }} value earned from {{ valuePeriods?.earnedValueStartMonth }} - {{ valuePeriods?.earnedValueEndMonth }}
            </template>
          </DollarValue>
          <DollarValue
            :value="props.currentQuarterValue"
            fontSize="1.375rem"
            :color="colorVidacorp"
            :privacy="true"
          >
            <template #caption>
              <div class="caption"><span>Latest Quarter</span></div></template
            >
          </DollarValue>
        </div>
      </div>
    </template>
    <template #2>
      <div class="opportunity">
        <slot class="title" name="opportunity-title" />
        <DollarValue :value="props.opportunityValue" fontSize="2.1875rem" :infoColor="props.division === 'DBG' ? colorBackgroundBright : colorText">
          <template #caption>
            <div
              @click="handleClick"
              class="caption"
              :class="{ hide: props.isMain || !props.path || isCurrentPath }"
            >
              <OutlinedDownIcon /><span>Unlock more value</span>
            </div>
          </template>
          <template #info>
            Total {{ props.division }} opportunity for next {{ valuePeriods?.opportunityPeriodsNr }} months
            calculated {{
              props.division === 'AXE' ? `for the missed claims from ${valuePeriods?.opportunityStartMonth} - ${valuePeriods?.opportunityMonth}` : `using the dispense usage of ${valuePeriods?.opportunityMonth} x ${valuePeriods?.opportunityPeriodsNr} months`
            }}
          </template>
        </DollarValue>
        <div></div>
      </div>
    </template>
  </Capsule>
</template>

<style scoped>
.value-outer,
.opportunity {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-around;
  height: 100%;
  position: relative;
  padding-block: 10%;
}

.value-inner {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-around;
}

.value.main {
  padding-top: 25%;
}

.dollar-value {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.caption {
  font-size: var(--font-size-caption);
  display: flex;
  align-items: center;
  gap: 0.3125rem;
  cursor: pointer;
}
.hide {
  visibility: hidden;
}
</style>
